// One-step shift close: count the drawer → variance → register the surplus as a fiscal WITHDRAW → close the till.
import React, { useEffect, useState } from 'react';
import { Banknote, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { apiCall } from './_shared';

const eur = (v) => `€${Number(v || 0).toFixed(2)}`;

const ShiftCloseModal = ({ onClose, onDone }) => {
  const [sum, setSum] = useState(null);
  const [counted, setCounted] = useState('');
  const [keep, setKeep] = useState('');
  const [notes, setNotes] = useState('');
  const [register, setRegister] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    apiCall('/api/admin/fiscal/shift/summary').then((s) => { setSum(s); setCounted(String(s.expected_closing)); setKeep(String(s.suggested_keep_float)); }).catch((e) => setErr(e.message));
  }, []);

  const c = Number(counted) || 0;
  const k = Number(keep) || 0;
  const variance = sum ? Math.round((c - sum.expected_closing) * 100) / 100 : 0;
  const withdraw = Math.max(Math.round((c - k) * 100) / 100, 0);

  const submit = async () => {
    if (!(c >= 0)) { setErr('Enter the counted cash'); return; }
    const msg = `Close the shift with ${eur(c)} counted${variance ? ` (variance ${eur(variance)})` : ''}${withdraw > 0 && register && sum?.ready ? ` and register a ${eur(withdraw)} withdrawal with the Tax Administration` : ''}?`;
    if (!window.confirm(msg)) return;
    setBusy(true); setErr('');
    try {
      const r = await apiCall('/api/admin/fiscal/shift/close', { method: 'POST', body: JSON.stringify({ counted_cash: c, keep_float: k, notes: notes || null, register_withdrawal: register }) });
      setResult(r); onDone && onDone(r);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const varCls = variance === 0 ? 'text-emerald-700' : variance > 0 ? 'text-blue-700' : 'text-red-700';

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md p-5 shadow-2xl space-y-3" onClick={(e) => e.stopPropagation()} data-testid="shift-close-modal">
        <h3 className="font-bold flex items-center gap-2"><Banknote className="w-5 h-5 text-orange-500" /> Close shift · count the drawer</h3>
        {!sum && !err && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading drawer expectation…</div>}
        {sum && !result && (
          <>
            <div className="grid grid-cols-3 gap-2 text-xs bg-slate-50 rounded-lg p-2" data-testid="shift-expectation">
              <div><div className="text-slate-500">Opening float</div><div className="font-semibold">{eur(sum.opening_float)}</div></div>
              <div><div className="text-slate-500">Cash taken</div><div className="font-semibold">+{eur(sum.cash_received_today)}</div></div>
              <div><div className="text-slate-500">Withdrawn</div><div className="font-semibold">−{eur(sum.withdrawn_today)}</div></div>
              <div className="col-span-3 border-t pt-1 flex justify-between"><span className="text-slate-500">Expected in drawer</span><b data-testid="shift-expected">{eur(sum.expected_closing)}</b></div>
            </div>
            <label className="block text-xs text-slate-600">Counted cash in drawer
              <input type="number" step="0.01" min="0" value={counted} onChange={(e) => setCounted(e.target.value)} className="mt-0.5 w-full border rounded px-3 py-2 text-lg font-bold" data-testid="shift-counted" />
            </label>
            <div className={`text-sm font-semibold ${varCls}`} data-testid="shift-variance">Variance: {variance > 0 ? '+' : ''}{eur(variance)}{variance === 0 ? ' · drawer balances' : variance > 0 ? ' · surplus' : ' · shortage'}</div>
            <div className="flex items-end gap-2">
              <label className="text-xs text-slate-600 flex-1">Float to keep for tomorrow
                <input type="number" step="0.01" min="0" value={keep} onChange={(e) => setKeep(e.target.value)} className="mt-0.5 w-full border rounded px-2 py-1.5 text-sm" data-testid="shift-keep" />
              </label>
              <div className="text-xs text-right"><div className="text-slate-500">Withdraw to safe</div><div className="font-bold text-slate-900 text-base" data-testid="shift-withdraw">{eur(withdraw)}</div></div>
            </div>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" className="w-full border rounded px-3 py-1.5 text-sm" data-testid="shift-notes" />
            <label className="flex items-center gap-2 text-xs text-slate-700">
              <input type="checkbox" checked={register} onChange={(e) => setRegister(e.target.checked)} disabled={!sum.ready} data-testid="shift-register-toggle" />
              Register the withdrawal with the Tax Administration (WITHDRAW cash deposit)
              {!sum.ready && <span className="text-amber-700">· fiscalization not configured</span>}
            </label>
            {err && <div className="text-xs text-red-600" data-testid="shift-error">{err}</div>}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm hover:bg-gray-100 rounded">Cancel</button>
              <button type="button" onClick={submit} disabled={busy} className="px-4 py-2 bg-orange-500 text-white rounded font-semibold text-sm inline-flex items-center gap-1 disabled:opacity-60" data-testid="shift-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Banknote className="w-4 h-4" />} Close shift
              </button>
            </div>
          </>
        )}
        {result && (
          <div className="space-y-2 text-sm" data-testid="shift-result">
            <div className="flex items-center gap-2 text-emerald-700 font-semibold"><CheckCircle2 className="w-5 h-5" /> Shift closed · counted {eur(result.counted_cash)} · variance {eur(result.variance)}</div>
            {result.withdrawal.status === 'ok' && <div className="text-xs bg-emerald-50 border border-emerald-200 rounded p-2">Withdrawal {eur(result.withdrawal.amount)} registered · FCDC <span className="font-mono">{result.withdrawal.fcdc}</span></div>}
            {result.withdrawal.status === 'failed' && <div className="text-xs bg-red-50 border border-red-200 rounded p-2 flex gap-1"><AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" /> Withdrawal {eur(result.withdrawal.amount)} was NOT accepted by EFI: {result.withdrawal.error}. Retry it from Finance → Fiscal Day.</div>}
            {result.withdrawal.status === 'skipped' && <div className="text-xs bg-slate-50 border rounded p-2">No withdrawal registered — {result.withdrawal.reason}.</div>}
            <div className="flex justify-end"><button type="button" onClick={onClose} className="px-4 py-2 bg-slate-900 text-white rounded text-sm font-semibold" data-testid="shift-done">Done</button></div>
          </div>
        )}
        {err && !sum && <div className="text-xs text-red-600">{err}</div>}
      </div>
    </div>
  );
};

export default ShiftCloseModal;
